import { Box } from '@factorialco/deprecated-design-system'

import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import i18n from 'modules/core/lib/i18n'

import type { ValidMainChurnReasons } from 'modules/billing/components/DeleteCompanyAction/_DeleteCompanyModal/types'

type Props = {
  mainChurnReason: ValidMainChurnReasons
  fieldValue: string | undefined
  fieldOnChange: (value: string) => void
}

const SecondaryChurnReason = ({ mainChurnReason, fieldValue, fieldOnChange }: Props) => {
  const SECONDARY_CHURN_REASONS: Record<ValidMainChurnReasons, { label: string; value: string }[]> =
    {
      'My company is closing': [],
      "My onboarding wasn't successful": [
        {
          label: i18n.t('billing.churn_request.options.bad_onboarding.secondary.complex_setup'),
          value: 'The set-up is too complicated',
        },
        {
          label: i18n.t('billing.churn_request.options.bad_onboarding.secondary.not_completed'),
          value: "I haven't completed onboarding",
        },
        {
          label: i18n.t('billing.churn_request.options.bad_onboarding.secondary.not_fit'),
          value: 'After onboarding, I realised that the tool was not a fit to my needs',
        },
      ],
      'I have experienced product issues': [
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.documents'),
          value: 'Documents',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.engagement'),
          value: 'Engagement',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.expenses'),
          value: 'Expenses',
        },
        {
          label: i18n.t(
            'billing.churn_request.options.product_issues.secondary.software_management'
          ),
          value: 'IT Management',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.mobile_app'),
          value: 'Mobile App',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.payroll'),
          value: 'Payroll',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.performance'),
          value: 'Performance',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.procurement'),
          value: 'Procurement',
        },
        {
          label: i18n.t(
            'billing.churn_request.options.product_issues.secondary.project_management'
          ),
          value: 'Project Management',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.recruitment'),
          value: 'Recruitment',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.reports'),
          value: 'Reports',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.shift_management'),
          value: 'Shift Management',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.space_management'),
          value: 'Space Management',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.time_off'),
          value: 'Time Off',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.time_tracking'),
          value: 'Time Tracking',
        },
        {
          label: i18n.t('billing.churn_request.options.product_issues.secondary.trainings'),
          value: 'Trainings',
        },
      ],
      "It's not the solution I need or was expecting": [
        {
          label: i18n.t('billing.churn_request.options.not_needed.secondary.missing_tools'),
          value: "I don't have the tools or integrations I need",
        },
        {
          label: i18n.t('billing.churn_request.options.not_needed.secondary.pricing'),
          value: 'The pricing is too high for the service provided',
        },
        {
          label: i18n.t('billing.churn_request.options.not_needed.secondary.not_enough_employees'),
          value: "My company doesn't have enough employees to use Factorial",
        },
        {
          label: i18n.t('billing.churn_request.options.not_needed.secondary.technological_tools'),
          value: "I don't have the right technological tools",
        },
      ],
      'Service received by the company was not up to standards': [
        {
          label: i18n.t('billing.churn_request.options.service_received.secondary.no_assistance'),
          value: "I haven't received the necessary assistance from Factorial",
        },
        {
          label: i18n.t('billing.churn_request.options.service_received.secondary.contact'),
          value: 'Too many changes of points of contact',
        },
        {
          label: i18n.t(
            'billing.churn_request.options.service_received.secondary.unsolved_problems'
          ),
          value: "The problems I reported haven't been fixed",
        },
        {
          label: i18n.t('billing.churn_request.options.service_received.secondary.bad_onboarding'),
          value: "The onboarding wasn't enough to know how to use the platform",
        },
        {
          label: i18n.t('billing.churn_request.options.service_received.secondary.documentation'),
          value: 'Factorial documentation was not appropriate',
        },
      ],
      'Management decision': [
        {
          label: i18n.t(
            'billing.churn_request.options.management_decision.secondary.cutting_costs'
          ),
          value: 'My company is cutting costs',
        },
        {
          label: i18n.t('billing.churn_request.options.management_decision.secondary.acquired'),
          value:
            'My company was acquired and my parent company decided to stop working with Factorial',
        },
        {
          label: i18n.t('billing.churn_request.options.management_decision.secondary.stop_working'),
          value: 'Management of my company decided to stop working with Factorial',
        },
      ],
      Other: [],
    }

  const options = SECONDARY_CHURN_REASONS[mainChurnReason].map((reason, index) => ({
    ...reason,
    id: index,
  }))
  const label =
    mainChurnReason === 'I have experienced product issues'
      ? i18n.t('billing.churn_request.secondary_feature_label')
      : i18n.t('billing.churn_request.secondary_option_label')

  switch (mainChurnReason) {
    case 'Other':
      return (
        <TextArea
          mandatory
          maxLength={500}
          onChange={fieldOnChange}
          label={label}
          value={fieldValue}
        />
      )
    default:
      return (
        <Box width="s400">
          <SelectField
            onChange={fieldOnChange}
            label={label}
            options={options}
            value={fieldValue}
            none={i18n.t('common.choose')}
            mandatory
          />
        </Box>
      )
  }
}

export default SecondaryChurnReason
